import { registryEntry, registryFlag, registryList } from '../registry';
import resolveValuePath from '../../../Helpers/resolveValuePath';
import FieldsWidget from './FieldsWidget';
import TabsWidget from './TabsWidget';

/**
 * The widgets a detail layout may place, and what each of them reads.
 *
 * Read by the layout editor to offer a region's options, and by the renderer to draw it.
 *
 *   usesEntries  - draws a list of keys, so the editor offers keys for it
 *   usesTitle    - one of those keys is its heading, so the editor offers to say which
 *   usesRegions  - holds regions of its own, so the editor lets you nest inside it
 *   allows       - what may be placed inside it, named by the widget that draws the candidate, so a
 *                  preset is allowed wherever the widget it draws through is; `*` is anything, and
 *                  saying nothing makes it a leaf
 *   required     - cannot be taken off the page; the editor offers no remove for it
 *   preview      - how the editor's canvas shows it: 'live' renders the widget itself,
 *                  'placeholder' draws a labelled block instead
 *   hasContent   - whether it has anything to show for a page, so a container may leave it out
 *   renderer     - the widget that draws it, where this one is a preset of another
 *   preset       - what the preset fills in on the region, which the region may still override
 */
/** Whether a collection of the record holds anything. */
const collectionFilled = (source, page) =>
  (resolveValuePath(page?.record, source) ?? []).length > 0;

export const WIDGET_TYPES = [
  {
    widget: 'fields',
    label: 'WIDGET_FIELDS',
    usesEntries: true,
    preview: 'live',
    // Worth drawing when it has keys to draw, and - where it reads a collection - when that
    // collection holds something.
    hasContent: (region, page) =>
      (region?.entries ?? []).length > 0 &&
      (!region?.source || collectionFilled(region.source, page)),
  },
  {
    widget: 'tabs',
    label: 'WIDGET_TABS',
    usesRegions: true,
    // A tab holds whatever a region can be, tabs included: what it shows fills it, so there is
    // nothing about a child it cannot hold.
    allows: ['*'],
    preview: 'live',
    // A container is worth drawing while any of its children is.
    hasContent: (region, page) =>
      (region?.regions ?? []).some((child) => widgetHasContent(child, page)),
  },
];

/**
 * Whether a widget draws a list of keys, so the editor offers keys for it.
 *
 * @param {String} widget - A widget name.
 *
 * @returns {Boolean}
 */
export const widgetUsesEntries = (widget) =>
  registryFlag(WIDGET_TYPES, 'widget', widget, 'usesEntries');

/**
 * Whether a widget holds regions of its own, so the editor lets you nest inside it.
 *
 * @param {String} widget - A widget name.
 *
 * @returns {Boolean}
 */
export const widgetUsesRegions = (widget) =>
  registryFlag(WIDGET_TYPES, 'widget', widget, 'usesRegions');

/**
 * What a widget shows where the layout says nothing, so the editor can list what is on the page
 * rather than an empty list beside a full card.
 *
 * @param {String} widget - A widget name.
 *
 * @returns {Array}
 */
export const widgetEntries = (widget) =>
  registryList(WIDGET_TYPES, 'widget', widget, 'defaultEntries');

/**
 * The widgets that may be placed inside this one, as the entries the editor offers for it.
 *
 * A candidate is judged by the widget that draws it rather than by its own name, so a preset is
 * offered wherever the widget it draws through is - including the presets saved after the container
 * was declared, which no list of names could have mentioned.
 *
 * A widget that allows nothing is a leaf, which is what saying nothing means. Recursive by
 * construction: what a child allows is asked of the child, so a container inside a container is
 * arranged the same way at any depth.
 *
 * @param {String} widget - A widget name.
 *
 * @returns {Array}
 */
export const childWidgets = (widget) => {
  const allows = registryList(WIDGET_TYPES, 'widget', widget, 'allows');

  if (allows.length === 0) return [];

  return WIDGET_TYPES.filter(
    (entry) =>
      allows.includes('*') || allows.includes(widgetRenderer(entry.widget))
  );
};

/**
 * Whether one of a widget's entries is its heading, so the editor offers to make one so.
 *
 * @param {String} widget - A widget name.
 *
 * @returns {Boolean}
 */
export const widgetUsesTitle = (widget) =>
  registryFlag(WIDGET_TYPES, 'widget', widget, 'usesTitle');

/**
 * Whether a widget has anything to show for a page, so a container may leave it out. A widget that
 * does not say always has.
 *
 * A preset that does not say is asked of the widget that draws it, and asked of the region as that
 * widget will receive it: what a preset reads is part of the preset, and a practice panel is empty
 * when there are no practices whether it says so itself or leaves its renderer to say it.
 *
 * @param {Object} region - The compiled region.
 * @param {Object} page - What the page publishes, as the widget would read it.
 *
 * @returns {Boolean}
 */
export const widgetHasContent = (region, page) => {
  // A widget nothing draws has nothing to show. Asked the way `RegionWidget` asks it, so a region
  // naming a widget this build does not hold leaves its place empty rather than taking room for a
  // paper with nothing in it.
  if (!WidgetComponents[widgetRenderer(region?.widget)]) return false;

  const entry = registryEntry(WIDGET_TYPES, 'widget', region?.widget);
  const holds =
    entry?.hasContent ??
    registryEntry(WIDGET_TYPES, 'widget', entry?.renderer)?.hasContent;

  if (!holds) return true;

  return Boolean(
    holds(entry?.preset ? { ...entry.preset, ...region } : region, page)
  );
};

/**
 * The widget that draws this one: itself, unless it is a preset of another.
 *
 * @param {String} widget - A widget name.
 *
 * @returns {String}
 */
export const widgetRenderer = (widget) =>
  registryEntry(WIDGET_TYPES, 'widget', widget)?.renderer ?? widget;

/**
 * What a preset fills in on the region it draws - the collection it reads, the room it leaves -
 * which the region itself may still override.
 *
 * @param {String} widget - A widget name.
 *
 * @returns {Object}
 */
export const widgetPreset = (widget) =>
  registryEntry(WIDGET_TYPES, 'widget', widget)?.preset ?? null;

/**
 * Whether a widget has to stay on the page.
 *
 * @param {String} widget - A widget name.
 *
 * @returns {Boolean}
 */
export const widgetIsRequired = (widget) =>
  registryFlag(WIDGET_TYPES, 'widget', widget, 'required');

/**
 * How the editor's canvas shows a widget - as itself, or as a labelled block.
 *
 * @param {String} widget - A widget name.
 *
 * @returns {String}
 */
export const widgetPreview = (widget) =>
  registryEntry(WIDGET_TYPES, 'widget', widget)?.preview ?? 'placeholder';

/**
 * Widget renderers, by the `widget` a region declares. The detail counterpart of `CellRenderers`.
 *
 * Each receives its compiled region and draws it; what it needs of the page around it - the record,
 * the handlers - it reads from `useDetailData`.
 */
export const WidgetComponents = {
  fields: FieldsWidget,
  tabs: TabsWidget,
};

/**
 * Adds a page's own widgets to the registry.
 *
 * `fields` and `tabs` are the ones any section can use, since they only read keys. A widget that
 * knows what it is drawing - a customer's identity, the call box - belongs to the page that owns it
 * and is declared there, so the engine holds no page's components.
 *
 * Called once, from the module the page imports: the page that renders a layout is the page that
 * registers what the layout can name.
 *
 * @param {Array} declarations - Entries as WIDGET_TYPES holds them, each with a `component`.
 */
export const registerWidgets = (declarations) =>
  (declarations ?? []).forEach(({ component, ...declaration }) => {
    // A preset names the widget that draws it rather than bringing a component of its own.
    if (!declaration.widget || !(component || declaration.renderer)) return;

    const held = WIDGET_TYPES.findIndex(
      (entry) => entry.widget === declaration.widget
    );

    if (held >= 0) WIDGET_TYPES.splice(held, 1, declaration);
    else WIDGET_TYPES.push(declaration);

    if (component) WidgetComponents[declaration.widget] = component;
  });

/**
 * What each applied preset stands in for, by the name it was applied under: the entry that was
 * there before it, or nothing where the name was free.
 */
const displaced = new Map();

/**
 * Puts the saved presets in the registry, in place of whatever they were standing in for.
 *
 * A preset is a registry entry like any other - the palette offers it, a layout names it, the
 * renderer draws it through the widget it names - the difference being that it arrives as a row
 * rather than as code. Applying a set replaces the last set applied, so a preset taken off the
 * server leaves the registry as it was before it arrived, whether that was nothing or the entry
 * checked in under the same name.
 *
 * @param {Array} declarations - Entries as WIDGET_TYPES holds them, each carrying a `presetId`.
 */
export const registerWidgetPresets = (declarations) => {
  const heldAt = (widget) =>
    WIDGET_TYPES.findIndex((entry) => entry.widget === widget);

  // The last set is undone first, so what follows is applied to the registry as the code left it
  // rather than to the one the last set made.
  displaced.forEach((entry, widget) => {
    const at = heldAt(widget);

    if (at < 0) return;

    if (entry) WIDGET_TYPES.splice(at, 1, entry);
    else WIDGET_TYPES.splice(at, 1);
  });

  displaced.clear();

  (declarations ?? []).forEach((declaration) => {
    if (!declaration.widget || !declaration.renderer) return;

    const at = heldAt(declaration.widget);

    displaced.set(declaration.widget, at >= 0 ? WIDGET_TYPES[at] : null);

    if (at >= 0) WIDGET_TYPES.splice(at, 1, declaration);
    else WIDGET_TYPES.push(declaration);
  });
};
